import { useEffect, useRef } from 'react';
import { useTeamChatUnread } from './useTeamChat';
import { useVoicePresence } from './useVoice';
import { useAuthStore } from '../store/useAuthStore';
import { msSinceLastPlayed, playSound } from '../lib/sounds';

/** A sound that just rang for the same event (push hand-off) is not repeated. */
const RECENT_MS = 20_000;

/**
 * The in-app sounds: a blip when a message arrives, another when someone walks
 * into a voice channel.
 *
 * Mounted ONCE, in the app shell, so the sounds follow you around the product
 * rather than only playing while the chat is on screen — which is the whole
 * point, since the chat being on screen is when you least need telling.
 *
 * Both are derived from polls that already run: the unread summary the sidebar
 * badge uses (15s), and the voice presence the channel list uses (10s). No new
 * requests, and nothing to unsubscribe from.
 *
 * Two rules keep it quiet when it should be:
 *
 *   The first poll of a session never plays. Otherwise every page load would
 *   announce yesterday's unread messages as if they just arrived.
 *
 *   Your own arrival is not announced to you. The server does not count your
 *   own message as unread either, so the message side needs no equivalent.
 */
export function useChatSounds(): void {
  const meId = useAuthStore((s) => s.user?.id ?? null);
  const { data: unread } = useTeamChatUnread();
  const { data: voice } = useVoicePresence();

  // `undefined` = nothing seen yet, which is different from "seen zero".
  const lastUnread = useRef<number | undefined>(undefined);
  const lastInVoice = useRef<Set<string> | undefined>(undefined);

  useEffect(() => {
    const total = unread?.total;
    if (total === undefined) return;

    const previous = lastUnread.current;
    lastUnread.current = total;
    if (previous === undefined) return;

    // Strictly greater: reading messages drives the count DOWN, and that is
    // not an event anybody wants a sound for.
    // A push for the same message may already have rung (lib/pushSounds.ts,
    // or the phone's notification): one sound per message, not two.
    if (total > previous && msSinceLastPlayed('message') > RECENT_MS) playSound('message');
  }, [unread?.total]);

  useEffect(() => {
    if (!voice?.enabled) return;

    const present = new Set<string>();
    for (const participants of Object.values(voice.channels ?? {})) {
      for (const p of participants) present.add(p.userId);
    }

    const previous = lastInVoice.current;
    lastInVoice.current = present;
    if (previous === undefined) return;

    for (const userId of present) {
      // Somebody who was not in any channel a moment ago now is. Moving
      // between two channels is deliberately not a join: they never left.
      if (!previous.has(userId) && userId !== meId) {
        if (msSinceLastPlayed('join') > RECENT_MS) playSound('join');
        break;
      }
    }
  }, [voice, meId]);
}
